import {
  forwardRef,
  MouseEvent,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { Button, Form, Select } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';

import { getCities, getStates, getSubCities } from '../../services/api';
import styles from './index.module.scss';

export interface SubDistrict {
  name: string;
  subDistrictId?: string;
}

export interface District {
  name: string;
  isAllSubDistrict: boolean;
  subDistricts: SubDistrict[];
  districtId?: string;
}

export interface ServiceAreaType {
  name: string;
  isAllDistrict: boolean;
  districts: District[];
  provinceId?: string;
  _id?: string;
}

export interface ServiceAreaInputType {
  data: ServiceAreaType[];
  onChange: Function;
  ref?: ServiceAreaRefType;
}

interface ProvinceOption {
  label: string;
  value: string;
}

export interface ServiceAreaRefType {
  populateData: () => void;
}

type PropTypes = ServiceAreaInputType;

const DEFAULT_SELECT_ALL = {
  label: 'เลือกทั้งหมด',
  value: '',
};

const ServiceAreaInput = forwardRef<ServiceAreaRefType, PropTypes>(
  (props: PropTypes, ref) => {
    const { data, onChange } = props;
    // console.log('data......', data);
    const [areas, setAreas] = useState<ServiceAreaType[]>([]);
    const [provinces, setProvinces] = useState<ProvinceOption[]>([]);
    const [districts, setDistricts] = useState<ProvinceOption[][]>([]);
    const [subDistricts, setSubDistricts] = useState<ProvinceOption[][]>([]);

    const [province, setProvince] = useState<string[]>([]);
    const [district, setDistrict] = useState<string[]>([]);
    const [subDistrict, setSubDistrict] = useState<string[]>([]);

    const [serviceAreas] = useMemo(() => {
      let servAreas = province.map((p: string, i: number) => {
        let prov = provinces.find((opt: ProvinceOption) => opt.value === p);
        let isAllDistrict = district[i] === '';
        let isAllSubDistrict = subDistrict[i] === '';

        let subD = isAllSubDistrict
          ? {
              label: 'ทั้งหมด',
              value: 'all',
            }
          : subDistricts[i]?.find(
              (opt: ProvinceOption) => opt.value === subDistrict[i]
            );

        let dist = isAllDistrict
          ? {
              label: 'ทั้งหมด',
              value: 'all',
            }
          : districts[i]?.find(
              (opt: ProvinceOption) => opt.value === district[i]
            );

        let d = [
          {
            // _id: dist?.value,
            name: dist?.label,
            districtId: dist?.value !== 'all' ? dist?.value : null,
            isAllSubDistrict,
            subDistricts: [
              {
                // _id: subD?.value,
                subDistrictId: subD?.value !== 'all' ? subD?.value : null,
                name: subD?.label,
              },
            ],
          },
        ];

        return {
          // _id: prov?.value,
          name: prov?.label,
          provinceId: prov?.value ? prov?.value : null,
          isAllDistrict,
          districts: d,
        };
      });

      return [servAreas];
    }, [province, district, subDistrict]);

    const handleAdd = (e: MouseEvent) => {
      e.preventDefault();

      let newAreas = Array.from(areas);
      newAreas.push({
        name: 'province',
        isAllDistrict: true,
        districts: [
          {
            name: 'district',
            isAllSubDistrict: true,
            subDistricts: [{ name: 'subdistrict' }],
          },
        ],
      });
      setAreas(newAreas);

      const newProvince = Array.from(province);
      newProvince.push('');
      setProvince(newProvince);

      const newDistricts = Array.from(districts);
      newDistricts.push([]);
      setDistricts(newDistricts);
      const newDistrict = Array.from(district);
      newDistrict.push('');
      setDistrict(newDistrict);

      const newSubDistricts = Array.from(subDistricts);
      newSubDistricts.push([]);
      setSubDistricts(newSubDistricts);
      const newSubDistrict = Array.from(subDistrict);
      newSubDistrict.push('');
      setSubDistrict(newSubDistrict);
    };

    const getProvinces = async () => {
      const provinces = await getStates();
      provinces.unshift(DEFAULT_SELECT_ALL);
      setProvinces(provinces);
    };

    const setProvinceValues = (value: string, index: number) => {
      const provinceIds = Array.from(province);
      provinceIds[index] = value;
      setProvince(provinceIds);
    };

    const setDistrictOptions = (value: ProvinceOption[], index: number) => {
      const newDistricts = Array.from(districts);
      newDistricts[index] = value;
      setDistricts(newDistricts);
    };

    const setDistrictsValue = (value: string, index: number) => {
      const newDistrictIds = Array.from(district);
      newDistrictIds[index] = value;
      setDistrict(newDistrictIds);
    };

    const setSubDistrictOptions = (value: ProvinceOption[], index: number) => {
      const newSubDistricts = Array.from(subDistricts);
      newSubDistricts[index] = value;
      setSubDistricts(newSubDistricts);
    };

    const setSubDistrictsValue = (value: string, index: number) => {
      const newSubDistrictIds = Array.from(subDistrict);
      newSubDistrictIds[index] = value;
      setSubDistrict(newSubDistrictIds);
    };

    const handleProvinceChange = async (e: string, areaIndex: number) => {
      setProvinceValues(e, areaIndex);

      const districtOptions = await getCities('', e);
      districtOptions.unshift(DEFAULT_SELECT_ALL);
      setDistrictOptions(districtOptions, areaIndex);
      setDistrictsValue('', areaIndex);

      setSubDistrictOptions([DEFAULT_SELECT_ALL], areaIndex);
      setSubDistrictsValue('', areaIndex);
    };

    const handleDistrictChange = async (e: string, areaIndex: number) => {
      setDistrictsValue(e, areaIndex);

      const subDistrictOptions = await getSubCities('', e);
      subDistrictOptions.unshift(DEFAULT_SELECT_ALL);

      setSubDistrictOptions(subDistrictOptions, areaIndex);
      setSubDistrictsValue('', areaIndex);
    };

    const handleSubDistrictChange = async (e: string, areaIndex: number) => {
      setSubDistrictsValue(e, areaIndex);
    };

    const deleteArea = (areaIndex: number) => {
      const newProvinceIds = Array.from(province).filter(
        (p: string, i: number) => areaIndex !== i
      );
      setProvince(newProvinceIds);

      const newDistrictOptions = Array.from(districts).filter(
        (d: object[], i: number) => areaIndex !== i
      );
      setDistricts(newDistrictOptions);

      const newDistrictIds = Array.from(district).filter(
        (d: string, i: number) => areaIndex !== i
      );
      setDistrict(newDistrictIds);

      const newSubDistrictOptions = Array.from(subDistricts).filter(
        (d: object[], i: number) => areaIndex !== i
      );
      setDistricts(newSubDistrictOptions);

      const newSubDistrictIds = Array.from(subDistrict).filter(
        (d: string, i: number) => areaIndex !== i
      );
      setDistrict(newSubDistrictIds);

      const newAreas = Array.from(areas).filter(
        (area: object, i: number) => areaIndex !== i
      );
      setAreas(newAreas);
    };

    const handletestChange = async () => {
      let newAreas = Array.from(areas);
      newAreas.push({
        name: 'province',
        isAllDistrict: true,
        districts: [
          {
            name: 'district',
            isAllSubDistrict: true,
            subDistricts: [
              {
                name: 'subdistrict',
              },
            ],
          },
        ],
      });
      setAreas(newAreas);
    };

    useImperativeHandle(ref, () => ({
      populateData,
    }));

    const populateData = async () => {
      if (data.length) {
        const provinceIds = Array.from(province);
        const districtIds = Array.from(district);
        const districtOptions = Array.from(districts);
        const subDistrictIds = Array.from(subDistrict);
        const subDistrictOptions = Array.from(subDistricts);

        let index: number = 0;
        for (const area of data) {
          index = data.findIndex((areaData) => areaData._id === area._id);
          districtOptions.push([DEFAULT_SELECT_ALL]);
          subDistrictOptions.push([DEFAULT_SELECT_ALL]);

          if (area.provinceId && !area.isAllDistrict) {
            provinceIds.push(area.provinceId);

            const districtData = await getCities('', area.provinceId);
            districtOptions[index] = [
              ...districtOptions[index],
              ...districtData,
            ];

            for (const d of area.districts) {
              if (d.districtId && !d.isAllSubDistrict) {
                districtIds.push(d.districtId);
                const subDistrictData = await getSubCities('', d.districtId);
                subDistrictOptions[index].push(...subDistrictData);

                d.subDistricts?.forEach((sd) => {
                  if (sd.subDistrictId) subDistrictIds.push(sd.subDistrictId);
                  else if (d.isAllSubDistrict) subDistrictIds.push('');
                });
              } else if (d.districtId && d.isAllSubDistrict) {
                districtIds.push(d.districtId);
                subDistrictIds.push('');
              }
            }
          } else if (area.provinceId && area.isAllDistrict) {
            provinceIds.push(area.provinceId);

            const districtData = await getCities('', area.provinceId);
            districtOptions[index] = [
              ...districtOptions[index],
              ...districtData,
            ];
            districtIds.push('');
            subDistrictIds.push('');
          }
        }
        setProvince(provinceIds);
        setDistricts(districtOptions);
        setDistrict(districtIds);
        setSubDistricts(subDistrictOptions);
        setSubDistrict(subDistrictIds);

        setAreas(data);
      }
    };

    useEffect(() => {
      onChange(serviceAreas);
    }, [serviceAreas]);

    useEffect(() => {
      getProvinces();
      // handletestChange();
    }, []);

    const Areas = () => {
      return (
        <>
          {areas.map((area, areaIndex) => (
            <div
              className={styles.serviceAreaInputRow}
              key={`area-${areaIndex}`}
            >
              <Form.Item
                label="จังหวัด"
                className={styles.serviceAreaInputSelect}
                rules={[
                  {
                    required: true,
                    message: 'กรุณาเลือกจังหวัด',
                  },
                ]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  defaultValue={''}
                  value={province[areaIndex]}
                  options={provinces}
                  onChange={(e) => handleProvinceChange(e, areaIndex)}
                ></Select>
              </Form.Item>
              <Form.Item
                label="อำเภอ/เขต"
                className={styles.serviceAreaInputSelect}
                rules={[
                  {
                    required: true,
                    message: 'กรุณาเลือกอำเภอ/เขต',
                  },
                ]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  defaultValue={''}
                  value={district[areaIndex]}
                  options={districts[areaIndex]}
                  onChange={(e) => handleDistrictChange(e, areaIndex)}
                ></Select>
              </Form.Item>
              <Form.Item
                className={styles.serviceAreaInputSelect}
                label="ตำบล/แขวง"
                rules={[
                  {
                    required: true,
                    message: 'กรุณาเลือกตำบล/แขวง',
                  },
                ]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  defaultValue=""
                  value={subDistrict[areaIndex]}
                  options={subDistricts[areaIndex]}
                  onChange={(e) => handleSubDistrictChange(e, areaIndex)}
                ></Select>
              </Form.Item>

              <div>
                <Button
                  size="small"
                  shape="circle"
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => deleteArea(areaIndex)}
                />
              </div>
            </div>
          ))}
        </>
      );
    };

    return (
      <>
        <div style={{ marginBottom: '1rem' }}>
          <Areas />
        </div>

        <Button
          block
          icon={<PlusOutlined />}
          onClick={(e: MouseEvent) => handleAdd(e)}
        >
          เพิ่มพื้นที่ให้บริการ
        </Button>
      </>
    );
  }
);

export default ServiceAreaInput;
